// Data
import { AdminTwin, Entity } from '@repo/frontend-types';

import { AddTwinEntityInput } from '@repo/backend-types';

// Utils
import { getVersionSuffix } from '@repo/front-end-utils';
import { prepareModelVersion } from '@repo/front-end-utils';

import {
  dgqInsertTwin,
  dgqDeleteTwinEntity,
  preserveMetadataDuringFmlUpload,
  extractSheetIdFromUrl,
} from '@repo/front-end-services';
import Config from '../../common/Config';
interface UpsertTwinWithMetadataPreservation {
  accessToken: string | undefined;
  orgBID: string | undefined;
  tree: AddTwinEntityInput;
  prevModel: Entity | undefined;
  updating: boolean;
  twin?: AdminTwin; // Twin object containing Google Sheet URL
  fmlContent?: Record<string, unknown>; // Optional original FML JSON content for Azure upload
  fmlFilename?: string; // Optional filename for Azure upload
  onMetadataPreservationUpdate?: (
    message: string,
    intent?: 'info' | 'success' | 'warning' | 'error',
  ) => void;
}

interface UpsertResult {
  success: boolean;
  error?: string;
  fmlFileUrl?: string;
  metadataPreservation?: {
    attempted: boolean;
    success: boolean;
    restoredCount: number;
    backupSheetId?: string;
    backupSheetUrl?: string;
    newSheetUrl?: string;
    newSheetId?: string;
    error?: string;
  };
}

interface MetadataPreservationResult {
  success: boolean;
  restoredCount: number;
  backupSheetId?: string;
  backupSheetUrl?: string;
  newSheetUrl?: string;
  newSheetId?: string;
  error?: string;
  fmlUploadSucceeded?: boolean;
  fmlFileUrl?: string;
}

/**
 * Runs the FML upload and preserves Google Sheets metadata. (The plain `upsertTwin` was removed with
 * Edit mode's Twin tab.)
 * This function creates a backup of the existing Google Sheet, performs the FML upload,
 * and then restores the metadata to the newly created entities.
 */
export const upsertTwinWithMetadataPreservation = async ({
  accessToken,
  orgBID,
  tree,
  prevModel,
  updating,
  twin,
  fmlContent,
  fmlFilename,
  onMetadataPreservationUpdate,
}: UpsertTwinWithMetadataPreservation): Promise<UpsertResult> => {
  if (!accessToken) {
    throw new Error('Invalid access token');
  }

  if (!orgBID) {
    throw new Error('Missing organisation bID');
  }

  // Check if we should attempt metadata preservation
  const shouldPreserveMetadata =
    twin?.google_sheet_url && extractSheetIdFromUrl(twin.google_sheet_url);

  if (shouldPreserveMetadata) {
    try {
      // Use the metadata preservation workflow
      const preservationResult: MetadataPreservationResult = await preserveMetadataDuringFmlUpload(
        Config.middlewareApi,
        accessToken,
        twin!.google_sheet_url!,
        tree.bID,
        async () => {
          // This callback performs the actual FML upload
          return await performFmlUpload({
            accessToken,
            orgBID,
            tree,
            prevModel,
            updating,
            fmlContent,
            fmlFilename,
          });
        },
        onMetadataPreservationUpdate, // Pass the callback for detailed feedback
      );

      // The detailed workflow provides progress feedback
      // Final success/failure messages are the caller's (FMLWorkflow), not this function's

      // Overall success is determined by whether FML upload succeeded, not by preservation success
      const overallSuccess = preservationResult.fmlUploadSucceeded ?? false;

      // Only set top-level error if FML upload failed, not if just preservation failed
      const topLevelError = overallSuccess ? undefined : preservationResult.error;

      return {
        success: overallSuccess,
        error: topLevelError,
        fmlFileUrl: preservationResult.fmlFileUrl,
        metadataPreservation: {
          attempted: true,
          success: preservationResult.success,
          restoredCount: preservationResult.restoredCount,
          backupSheetId: preservationResult.backupSheetId,
          backupSheetUrl: preservationResult.backupSheetUrl,
          newSheetUrl: preservationResult.newSheetUrl,
          newSheetId: preservationResult.newSheetId,
          error: preservationResult.error,
        },
      };
    } catch (error) {
      // If metadata preservation fails completely, fall back to normal upload
      console.error('Metadata preservation failed, falling back to normal upload:', error);
      onMetadataPreservationUpdate?.(
        `Metadata preservation failed, proceeding with normal upload. Metadata will be lost.`,
        'warning',
      );

      try {
        const fmlResult = await performFmlUpload({
          accessToken,
          orgBID,
          tree,
          prevModel,
          updating,
          fmlContent,
          fmlFilename,
        });

        return {
          success: fmlResult.success,
          error: fmlResult.error,
          fmlFileUrl: fmlResult.fmlFileUrl,
          metadataPreservation: {
            attempted: true,
            success: false,
            restoredCount: 0,
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        };
      } catch (fmlError) {
        return {
          success: false,
          error: fmlError instanceof Error ? fmlError.message : 'FML upload failed',
          metadataPreservation: {
            attempted: true,
            success: false,
            restoredCount: 0,
            error: fmlError instanceof Error ? fmlError.message : 'Unknown error',
          },
        };
      }
    }
  } else {
    // No Google Sheet or metadata to preserve, perform normal upload
    if (!twin?.google_sheet_url) {
      onMetadataPreservationUpdate?.(
        'No Google Sheet found, proceeding with normal upload.',
        'info',
      );
    }

    try {
      const fmlResult = await performFmlUpload({
        accessToken,
        orgBID,
        tree,
        prevModel,
        updating,
        fmlContent,
        fmlFilename,
      });

      return {
        success: fmlResult.success,
        error: fmlResult.error,
        fmlFileUrl: fmlResult.fmlFileUrl,
        metadataPreservation: {
          attempted: false,
          success: false,
          restoredCount: 0,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'FML upload failed',
        fmlFileUrl: undefined,
        metadataPreservation: {
          attempted: false,
          success: false,
          restoredCount: 0,
        },
      };
    }
  }
};

/**
 * Performs the actual FML upload (delete + insert) without metadata preservation.
 * This is the FML half of the former `upsertTwin`, kept when the rest went with Edit mode's Twin tab.
 */
async function performFmlUpload({
  accessToken,
  orgBID,
  tree,
  prevModel,
  updating,
  fmlContent,
  fmlFilename,
}: {
  accessToken: string;
  orgBID: string;
  tree: AddTwinEntityInput;
  prevModel: Entity | undefined;
  updating: boolean;
  fmlContent?: Record<string, unknown>;
  fmlFilename?: string;
}): Promise<{ success: boolean; error?: string; fmlFileUrl?: string }> {
  tree.organisation = prevModel?.organisation; // Ensure we set the new model's organisation property

  let updated = false;
  let inserted = false;

  // If updating, attempt to create a back-up of the previous model
  if (updating) {
    try {
      if (prevModel) {
        const versionSuffix = getVersionSuffix();
        // Parse the prevModel and apply version to bIDs
        const modelVersion = prepareModelVersion(prevModel, versionSuffix);
        // Insert the modelVersion
        const updateResult = await dgqInsertTwin(
          Config.middlewareApi,
          accessToken,
          orgBID,
          modelVersion,
        );
        if (!updateResult.success) {
          return {
            success: false,
            error: updateResult.error || 'Failed to create backup version of previous model',
          };
        }
        updated = true;
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to create backup version of previous model',
      };
    }
  }

  // Delete the existing twin
  const deleteResult = await dgqDeleteTwinEntity(
    Config.middlewareApi,
    accessToken,
    orgBID,
    tree.bID,
    true, // Ensure we delete the entire old model/tree
  );

  if (!deleteResult.success) {
    return {
      success: false,
      error: deleteResult.error || 'Failed to delete existing twin from Dgraph',
    };
  }

  // Insert new twin
  const insertResult = await dgqInsertTwin(Config.middlewareApi, accessToken, orgBID, tree);

  if (!insertResult.success) {
    return { success: false, error: insertResult.error || 'Failed to insert new twin into Dgraph' };
  }
  inserted = true;

  // Check final result
  const success = updating ? updated && inserted : inserted;

  // Upload FML file to Azure Storage after successful Dgraph save
  let fmlFileUrl: string | undefined;
  if (success && fmlContent) {
    try {
      const { uploadFmlFile } = await import('@repo/front-end-services');
      const uploadResult = await uploadFmlFile(Config.middlewareApi, accessToken, {
        twin_bid: tree.bID,
        fml_content: fmlContent,
        filename: fmlFilename,
      });
      fmlFileUrl = uploadResult.file_url;
      console.log(`Successfully uploaded FML file to Azure Storage for twin ${tree.bID}`);
    } catch (error) {
      // Log error but don't fail the upload - Azure upload is secondary to Dgraph save
      console.error(`Failed to upload FML file to Azure Storage for twin ${tree.bID}:`, error);
    }
  }

  return { success, fmlFileUrl };
}
